import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { noAuthGuard } from './core/guards/noAuth.guard';
import { LayoutComponent } from './layouts/layout.component';
import { DataResolver } from './core/resolver/data.resolver';
import { appCodeRedirectGuard } from './core/guards/app-code-redirect.guard';
import { guestGuard } from './core/guards/guest.guard';
import { ScreenRenderingMode } from './core/const/constants';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [appCodeRedirectGuard],
    children: [] // This empty children array is necessary for the canActivate to work on the root path
  },

  // Guest routes
  {
    path: 'sign-in',
    canActivate: [noAuthGuard],
    canActivateChild: [noAuthGuard],
    loadComponent: () => import('./features-modules/auth/sign-in/sign-in.component').then(m => m.SignInComponent)
  },

  {
    path: 'sign-up',
    canActivate: [noAuthGuard],
    canActivateChild: [noAuthGuard],
    loadComponent: () => import('./features-modules/auth/sign-up/sign-up.component').then(m => m.SignUpComponent)
  },

  // Document routes
  {
    path: 'documents',
    canActivate: [guestGuard],
    canActivateChild: [guestGuard],
    children: [
      {
        path: ':id',
        loadComponent: () => import('./core/documents/document/document.component').then(m => m.DocumentComponent)
      },
      {
        path: '',
        loadComponent: () => import('./core/documents/document/document.component').then(m => m.DocumentComponent)
      }
    ]
  },

  // Admin routes
  {
    path: 'admin',
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    component: LayoutComponent,
    resolve: { resolvedData: DataResolver },
    data: { routeType: 'Admin' },
    children: [
      {
        path: ':screenId',
        loadComponent: () =>
          import('./features-modules/formio/tab-form-renderer/tab-form-renderer.component').then(
            m => m.TabFormRendererComponent
          ),
        data: { screenRenderingMode: ScreenRenderingMode.MainScreen }
      },
      {
        path: '',
        loadComponent: () =>
          import('./features-modules/formio/tab-form-renderer/tab-form-renderer.component').then(
            m => m.TabFormRendererComponent
          ),
        data: { screenRenderingMode: ScreenRenderingMode.MainScreen }
      }
    ]
  },

  // App initialize routes
  {
    path: 'initializing',
    canActivate: [guestGuard],
    loadComponent: () => import('./features-modules/auth/initializing/initializing.component').then(m => m.InitializingComponent)
  },

  // Document routes
  {
    path: ':appCode/documents',
    canActivate: [guestGuard],
    canActivateChild: [guestGuard],
    children: [
      {
        path: ':id',
        loadComponent: () => import('./core/documents/document/document.component').then(m => m.DocumentComponent)
      },
      {
        path: '',
        loadComponent: () => import('./core/documents/document/document.component').then(m => m.DocumentComponent)
      }
    ]
  },

  // App guest routes
  {
    path: ':appCode',
    canActivate: [noAuthGuard],
    canActivateChild: [noAuthGuard],
    children: [
      {
        path: 'sign-in',
        loadComponent: () => import('./features-modules/auth/sign-in/sign-in.component').then(m => m.SignInComponent)
      },
      { path: '', redirectTo: 'sign-in', pathMatch: 'full' }
    ]
  },

  // App routes
  {
    path: ':appCode',
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    component: LayoutComponent,
    resolve: { resolvedData: DataResolver },
    children: [
      {
        path: 'change-password',
        loadComponent: () =>
          import('./features-modules/auth/change-password/change-password.component').then(
            m => m.ChangePasswordComponent
          )
      },
      {
        path: 'screen',
        children: [
          {
            path: ':screenId',
            loadComponent: () =>
              import('./features-modules/formio/tab-form-renderer/tab-form-renderer.component').then(
                m => m.TabFormRendererComponent
              ),
            data: { screenRenderingMode: ScreenRenderingMode.MainScreen }
          },
          {
            path: '',
            loadComponent: () =>
              import('./features-modules/formio/tab-form-renderer/tab-form-renderer.component').then(
                m => m.TabFormRendererComponent
              ),
            data: { screenRenderingMode: ScreenRenderingMode.MainScreen }
          }
        ]
      }
    ]
  },

  // Organization setup routes
  {
    path: ':appCode',
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    resolve: { resolvedData: DataResolver },
    children: [
      {
        path: 'org-setup',
        children: [
          {
            path: ':screenId',
            loadComponent: () =>
              import('./features-modules/formio/tab-form-renderer/tab-form-renderer.component').then(
                m => m.TabFormRendererComponent
              ),
            data: { screenRenderingMode: ScreenRenderingMode.MainScreen }
          },
          { path: '', redirectTo: '/sign-in', pathMatch: 'full' }
        ]
      }
    ]
  },

  {
    path: '**',
    canActivate: [appCodeRedirectGuard],
    children: [] // This empty children array is necessary for the canActivate to work on the root path
  }
];
